import { EventBus } from '@pp/toolkit';

import {
  type HostEventName,
  type HostEventPayload,
  VideoPlayerEvent,
  type VideoPlayerEventName,
  type VideoPlayerEventPayload,
  VideoPlayerHandshakeEvent,
} from './types';

type MessageListener<T> = (detail: T) => void;

export class VideoServiceBus extends EventBus<{
  ready: () => void;
}> {
  ready: boolean = false;
  private container: HTMLElement | null = null;

  videoPlayerEventListeners: {
    [K in keyof VideoPlayerEventPayload]?: Array<MessageListener<VideoPlayerEventPayload[K]>>;
  } = {};

  constructor(private document: Document) {
    super();

    Object.values(VideoPlayerEvent).forEach((eventName) => {
      this.attachVideoPlayerEventListener(eventName);
    });
  }

  attachVideoPlayerEventListener<EventName extends VideoPlayerEventName>(eventName: EventName): void {
    const listener = ((event: CustomEvent<VideoPlayerEventPayload[EventName]>) => {
      const { detail } = event;
      this.videoPlayerEventListeners[eventName]?.forEach((callback) => callback(detail));
    }) as EventListener;
    this.document.addEventListener(eventName, listener);
  }

  onVideoPlayerEvent<K extends VideoPlayerEventName>(
    eventName: K,
    callback: MessageListener<VideoPlayerEventPayload[K]>
  ): void {
    this.videoPlayerEventListeners[eventName] ??= [];
    this.videoPlayerEventListeners[eventName].push(callback);
  }

  setVideoContainer(container: HTMLElement): void {
    if (this.container === container) return;
    this.container = container;
    this.handshake(container);
  }

  handshake(container: HTMLElement): void {
    if (this.ready) return;
    const sendContainer = (): void => {
      this.document.dispatchEvent(
        new CustomEvent(VideoPlayerHandshakeEvent.HostContainer, {
          detail: {
            container: container,
          },
        })
      );
    };

    const handleReady = (): void => {
      this.ready = true;
      this.dispatchEvent('ready');
      this.document.removeEventListener(VideoPlayerHandshakeEvent.Ready, handleReady);
      this.document.removeEventListener(VideoPlayerHandshakeEvent.AwaitingContainer, sendContainer);
    };

    this.document.addEventListener(VideoPlayerHandshakeEvent.AwaitingContainer, sendContainer);
    this.document.addEventListener(VideoPlayerHandshakeEvent.Ready, handleReady);

    sendContainer();
  }

  dispatchVideoPlayerEvent<K extends HostEventName>(eventName: K, data?: HostEventPayload[K]): void {
    this.document.dispatchEvent(new CustomEvent(eventName, { detail: data }));
  }

  reset(): void {
    super.clear();
    this.ready = false;
    this.videoPlayerEventListeners = {};
    this.container = null;
  }
}
